import { Size } from '~/lib/ApiModel';
import { IApiClient } from '~/lib/ApiClient';
import { loadAllItems } from '~/lib/helper/PagingHelper';
import { AsyncStore } from './AsyncStore';
import { computed, observable, action, makeObservable } from 'mobx';
import { hasSameSequence } from '../helper/ArrayHelper';
import { HotSwappablePromise, CancellablePromise } from '../helper/PromiseHelper';

export class SizesStore extends AsyncStore<Size[]> {
    @observable
    serverId?: number = undefined;

    @observable
    osSlug?: string;

    request?: HotSwappablePromise<Size[]> = undefined;
    requestKey?: unknown[] = undefined;

    constructor(private apiClient: IApiClient) {
        super(() => this.loadSizes(), []);
        makeObservable(this);
    }

    loadSizes(): Promise<Size[]> {
        const serverId = this.serverId;
        const osSlug = this.osSlug;

        const key = [serverId, osSlug];

        // if we are after the same things, we can piggy back on the same request
        if (this.request && this.requestKey && hasSameSequence(this.requestKey, key)) {
            return this.request.result;
        }

        const cleanUp = () => {
            this.request = undefined;
            this.requestKey = undefined;
        };

        const req = new CancellablePromise((token) =>
            loadAllItems((link) => this.apiClient.getSizes(serverId, osSlug, link, token))
        );
        this.requestKey = key;
        if (this.request) {
            this.request.swap(req);
        } else {
            this.request = new HotSwappablePromise(req);
            this.request.result.then(cleanUp, cleanUp);
        }

        return this.request.result;
    }

    @action
    setOs(osSlug?: string) {
        this.osSlug = osSlug;
        void this.load();
    }

    @action
    loadWithParams(serverId?: number, osSlug?: string): Promise<Size[]> {
        this.serverId = serverId;
        this.osSlug = osSlug;
        return this.load();
    }

    @computed
    get items() {
        return this.item;
    }
}
